import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, CircularProgress, Box } from '@mui/material';
import {
  DirectionsBus,
  Add,
  CalendarMonth,
  People,
  CheckCircle,
  Visibility,
  Cancel,
  Search,
} from '@mui/icons-material';
import ServiceAuth from '../../components/ServiceAuth';
import ServiceDashboard from '../../components/services/ServiceDashboard';
import ServiceHeader from '../../components/services/ServiceHeader';
import ShuttleBookingForm from '../../components/shuttle/ShuttleBookingForm';
import BusSeatingModal from '../../components/shuttle/BusSeatingModal';
import api from '../../services/api';
import { format } from 'date-fns';

const ShuttleServicePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isServiceAuthenticated, setIsServiceAuthenticated] = useState(false);
  const [serviceUser, setServiceUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>({
    stats: [],
    recentItems: [],
  });
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [seatingModalOpen, setSeatingModalOpen] = useState(false);
  const [selectedScheduleForSeating, setSelectedScheduleForSeating] = useState<any>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/shuttle/my-bookings');
      
      // The shuttles.py endpoint returns {bookings: [...]}
      const bookings = response.data?.bookings || response.data || [];
      
      const now = new Date();
      const upcomingBookings = bookings.filter((b: any) => {
        const depDate = b.schedule?.departure_date || b.departure_date;
        if (!depDate) return false;
        try {
          const departureDate = new Date(depDate);
          return !isNaN(departureDate.getTime()) && departureDate >= now;
        } catch {
          return false;
        }
      });
      const confirmedBookings = bookings.filter((b: any) => b.status === 'confirmed');
      
      // Calculate stats
      const stats = [
        {
          label: t('shuttle.dashboard.totalBookings'),
          value: bookings.length,
          icon: <DirectionsBus sx={{ color: 'primary.main' }} />,
        },
        {
          label: t('shuttle.dashboard.upcomingTrips'),
          value: upcomingBookings.length,
          icon: <CalendarMonth sx={{ color: 'success.main' }} />,
          color: 'success.main',
        },
        {
          label: t('shuttle.dashboard.confirmedBookings'),
          value: confirmedBookings.length,
          icon: <CheckCircle sx={{ color: 'info.main' }} />,
        },
        {
          label: t('shuttle.dashboard.totalSeats'),
          value: bookings.reduce((sum: number, b: any) => sum + (b.seats_booked || 0), 0),
          icon: <People sx={{ color: 'warning.main' }} />,
        },
      ];

      // Format recent items
      const recentItems = bookings.slice(0, 5).map((booking: any) => {
        // Extract data - handle both nested (from some endpoints) and flat structure
        const schedule = booking.schedule || {};
        const departureDate = booking.departure_date || schedule.departure_date;
        const departureTime = booking.departure_time || schedule.departure_time;
        const rawPickup = booking.pickup_location || schedule.pickup_location;
        const rawDestination = booking.destination || schedule.destination;
        // Use fallback values if translation keys don't resolve
        // For paragliding: pickup is at landing zone, destination is at launch/takeoff
        const pickupLocation = rawPickup && rawPickup.trim() ? rawPickup : (t('shuttle.defaultPickup', 'Landing'));
        const destination = rawDestination && rawDestination.trim() ? rawDestination : (t('shuttle.defaultDestination', 'Launch'));
        const shuttleName = booking.shuttle_name || schedule.shuttle_name;
        
        // Safely parse the date
        let dateStr = 'Date not available';
        let canCancel = false;
        
        if (departureDate) {
          try {
            const depDate = new Date(departureDate);
            if (!isNaN(depDate.getTime())) {
              dateStr = format(depDate, 'PP');
              canCancel = booking.status === 'confirmed' && depDate >= now;
            }
          } catch (e) {
            console.error('Error parsing date:', departureDate, e);
          }
        }
        
        // Format time
        const timeStr = departureTime ? 
          (typeof departureTime === 'string' ? 
            departureTime.substring(0, 5) : 
            departureTime) : 
          '';
        
        // Format booking code more prominently
        const bookingCode = booking.booking_code || booking.id;
        const seatText = booking.seats_booked === 1 ? t('shuttle.passengers.seat', 'seat') : t('shuttle.seats', 'seats');
        
        return {
          title: `${bookingCode} • ${pickupLocation} → ${destination}`,
          subtitle: `📅 ${dateStr}${timeStr ? ` ${t('common.at', 'at')} ${timeStr}` : ''} • 👥 ${booking.seats_booked || 0} ${seatText}${shuttleName ? ` • 🚐 ${shuttleName}` : ''}`,
          status: booking.status || 'unknown',
          statusColor: getStatusColor(booking.status || 'unknown'),
          actions: [
            {
              label: t('common.view', 'View'),
              icon: <Visibility sx={{ fontSize: 16 }} />,
              onClick: () => navigate(`/shuttle-booking/${booking.booking_code || booking.id}`),
              variant: 'text' as const,
              color: 'primary' as const,
              size: 'small' as const,
              compact: true,
            },
            {
              label: t('shuttle.passengers.seats', 'Seats'),
              icon: <People sx={{ fontSize: 16 }} />,
              onClick: () => {
                setSelectedScheduleForSeating({
                  scheduleId: booking.schedule_id || booking.schedule?.id,
                  shuttleName: shuttleName,
                  departureDate: departureDate,
                  departureTime: departureTime,
                  pickupLocation: pickupLocation,
                  destination: destination,
                  currentBooking: {
                    guest_nickname: booking.guest_nickname,
                    guest_name: booking.guest_name,
                    seats_booked: booking.seats_booked,
                  },
                });
                setSeatingModalOpen(true);
              },
              variant: 'text' as const,
              color: 'secondary' as const,
              size: 'small' as const,
              compact: true,
            },
            canCancel && {
              label: t('common.cancel', 'Cancel'),
              icon: <Cancel sx={{ fontSize: 16 }} />,
              onClick: () => handleCancelBooking(booking.id),
              variant: 'text' as const,
              color: 'error' as const,
              size: 'small' as const,
              compact: true,
            },
          ].filter(Boolean),
        };
      });

      setDashboardData({ stats, recentItems });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // If API fails, set empty data
      setDashboardData({
        stats: [
          {
            label: t('shuttle.dashboard.totalBookings'),
            value: 0,
            icon: <DirectionsBus sx={{ color: 'primary.main' }} />,
          },
          {
            label: t('shuttle.dashboard.upcomingTrips'),
            value: 0,
            icon: <CalendarMonth sx={{ color: 'success.main' }} />,
            color: 'success.main',
          },
        ],
        recentItems: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have service authentication
    const checkAuth = () => {
      const serviceToken = localStorage.getItem('serviceToken');
      const serviceUserData = localStorage.getItem('serviceUserData');
      
      if (serviceToken && serviceUserData) {
        setIsServiceAuthenticated(true);
        setServiceUser(JSON.parse(serviceUserData));
        fetchDashboardData();
      } else {
        setLoading(false);
      }
    };
    
    // Check on mount
    checkAuth();
    
    // Listen for auth changes
    const handleAuthChange = () => {
      checkAuth();
    };
    
    window.addEventListener('serviceAuthChanged', handleAuthChange);
    
    return () => {
      window.removeEventListener('serviceAuthChanged', handleAuthChange);
    };
  }, []); // Don't re-run on fetchDashboardData change

  const handleAuthenticated = (userData: any) => {
    setIsServiceAuthenticated(true);
    setServiceUser(userData);
    fetchDashboardData();
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (window.confirm(t('shuttle.dashboard.confirmCancel'))) {
      try {
        await api.post(`/shuttle/cancel-booking/${bookingId}`);
        fetchDashboardData();
      } catch (error) {
        console.error('Failed to cancel booking:', error);
      }
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getUserTier = () => {
    if (serviceUser?.has_premium_access || serviceUser?.service_tier === 'premium') {
      return 'premium';
    }
    return 'free';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!isServiceAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ServiceAuth 
          onAuthenticated={handleAuthenticated}
          intent="shuttle"
        />
      </Container>
    );
  }

  const quickActions = [
    {
      label: t('shuttle.dashboard.bookNewTrip'),
      icon: <Add />,
      onClick: () => setBookingFormOpen(true),
      variant: 'contained' as const,
    },
    {
      label: t('shuttle.dashboard.searchBooking'),
      icon: <Search />,
      onClick: () => {
        const code = prompt(t('shuttle.dashboard.enterBookingCode'));
        if (code) {
          navigate(`/shuttle-booking/${code}`);
        }
      },
      variant: 'outlined' as const,
    },
  ];

  const features = [
    {
      title: t('shuttle.features.onlineBooking'),
      description: t('shuttle.features.onlineBookingDesc'),
      available: true,
    },
    {
      title: t('shuttle.features.seatSelection'),
      description: t('shuttle.features.seatSelectionDesc'),
      available: getUserTier() === 'premium',
    },
    {
      title: t('shuttle.features.priorityBooking'),
      description: t('shuttle.features.priorityBookingDesc'),
      available: getUserTier() === 'premium',
    },
    {
      title: t('shuttle.features.flexibleCancellation'),
      description: t('shuttle.features.flexibleCancellationDesc'),
      available: getUserTier() === 'premium',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      {/* Service Header */}
      <ServiceHeader 
        serviceId="shuttle"
      />
      
      <ServiceDashboard
        serviceId="shuttle"
        stats={dashboardData.stats}
        recentItems={dashboardData.recentItems}
        quickActions={quickActions}
        features={features}
        userTier={getUserTier()}
        upgradeMessage={t('shuttle.dashboard.upgradeMessage')}
        emptyStateMessage={t('shuttle.dashboard.noBookings')}
        emptyStateAction={{
          label: t('shuttle.dashboard.makeFirstBooking'),
          onClick: () => setBookingFormOpen(true),
        }}
      />
      
      {/* Booking Form Dialog */}
      <ShuttleBookingForm
        open={bookingFormOpen}
        onClose={() => setBookingFormOpen(false)}
        onSuccess={() => {
          setBookingFormOpen(false);
          fetchDashboardData();
        }}
        userEmail={serviceUser?.email}
        userName={serviceUser?.name}
      />
      
      {/* Bus Seating Modal */}
      {selectedScheduleForSeating && (
        <BusSeatingModal
          open={seatingModalOpen}
          onClose={() => {
            setSeatingModalOpen(false);
            setSelectedScheduleForSeating(null);
          }}
          scheduleId={selectedScheduleForSeating.scheduleId}
          shuttleName={selectedScheduleForSeating.shuttleName}
          departureDate={selectedScheduleForSeating.departureDate}
          departureTime={selectedScheduleForSeating.departureTime}
          pickupLocation={selectedScheduleForSeating.pickupLocation}
          destination={selectedScheduleForSeating.destination}
          currentBooking={selectedScheduleForSeating.currentBooking}
        />
      )}
    </Container>
  );
};

export default ShuttleServicePage;